import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { TreeViewProps } from '@mui/lab';
import { styled } from '@mui/material';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';

import CheckboxTreeItem from './CheckboxTreeItem';

interface EmployeeCheckboxTreeProps extends TreeViewProps<true> {
  items: TreeViewBaseItem[];
  selectedItemIds: string[];
  setSelectedItemIds: Dispatch<SetStateAction<string[]>>;
  /** 단일/다중 선택 여부 (default: true) */
  multiple?: boolean;
}

// 리프(= 직원) 노드 id 집합 추출. 본부/부서 노드는 선택 대상에서 제외하기 위함
function collectLeafIds(nodes: TreeViewBaseItem[], out: Set<string> = new Set()): Set<string> {
  nodes.forEach((node) => {
    if (node.children && node.children.length > 0) {
      collectLeafIds(node.children, out);
    } else {
      out.add(String(node.id));
    }
  });
  return out;
}

/**
 * 직원 조회 모달 전용 체크박스 트리.
 * - 본부/부서 노드는 클릭해도 선택되지 않음 (라벨은 노출, 체크박스 없음)
 * - 리프(직원) 노드만 토글 가능
 */
const EmployeeCheckboxTree = ({
  items,
  selectedItemIds,
  setSelectedItemIds,
  multiple = true,
  ...props
}: EmployeeCheckboxTreeProps) => {
  const leafIds = useMemo(() => collectLeafIds(items), [items]);

  const handleItemSelectionToggle = (
    _e: React.SyntheticEvent<Element, Event>,
    selected: string[] | string | null,
  ) => {
    if (typeof selected !== 'string') return;
    // 리프가 아니면(부서/본부) 무시
    if (!leafIds.has(selected)) return;

    const isSelected = selectedItemIds.includes(selected);
    if (isSelected) {
      setSelectedItemIds(selectedItemIds.filter((sid) => sid !== selected));
    } else if (multiple) {
      setSelectedItemIds([...selectedItemIds, selected]);
    } else {
      setSelectedItemIds([selected]);
    }
  };

  // 부서 id가 실수로 유입되더라도 트리에는 리프만 선택 상태로 노출
  const visibleSelected = useMemo(
    () => selectedItemIds.filter((id) => leafIds.has(id)),
    [selectedItemIds, leafIds],
  );

  return (
    <StyledTreeView
      items={items}
      selectedItems={visibleSelected}
      onSelectedItemsChange={handleItemSelectionToggle}
      slots={{
        item: CheckboxTreeItem,
      }}
      slotProps={{
        item: (p) => {
          return {
            isSelect: visibleSelected.includes((p as { itemId: string }).itemId),
          } as any;
        },
      }}
      {...props}
    />
  );
};

export default EmployeeCheckboxTree;

const StyledTreeView = styled(RichTreeView)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1),
}));
